import type { CSSProperties } from 'react';
import styles from './Field.module.css';

interface FieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * `text` for names, `kip` for money, `qty` for quantities.
   *
   * Never `<input type="number">`, whatever the field holds. It gives
   * scroll-wheel mutation, spinner chrome, `valueAsNumber` of NaN on partial
   * input, and locale-dependent decimal parsing. The keyboard is chosen with
   * `inputMode` instead, and the string is parsed by `parseKip`/`parseQty`.
   */
  kind?: 'text' | 'kip' | 'qty' | 'tel';
  /**
   * Masks the value and stops the browser suggesting it back.
   *
   * Separate from `kind`, which chooses the keyboard: a password is ordinary
   * text as far as input mode goes, and a shop phone is shared enough that
   * typing one in the clear at the counter is a real exposure.
   */
  secret?: boolean;
  label?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  onEnter?: () => void;
}

const INPUT_MODE = {
  text: undefined,
  kip: 'numeric',
  qty: 'decimal',
  tel: 'tel',
} as const;

export function Field({
  value,
  onChange,
  placeholder,
  kind = 'text',
  secret = false,
  label,
  autoFocus,
  disabled,
  className,
  style,
  onEnter,
}: FieldProps) {
  return (
    <input
      type={secret ? 'password' : 'text'}
      autoComplete={secret ? 'current-password' : undefined}
      inputMode={secret ? undefined : INPUT_MODE[kind]}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && onEnter) onEnter();
      }}
      placeholder={placeholder}
      aria-label={label ?? placeholder}
      autoFocus={autoFocus}
      disabled={disabled}
      className={[styles.field, kind === 'text' ? styles.lao : styles.mono, className]
        .filter(Boolean)
        .join(' ')}
      style={style}
    />
  );
}
